import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SignOrderService } from '../services/sign-order.service';
import { OrderListItem, SignOrderDraftSummary } from '../models/sign-order.models';
import { formatSignOrderDate } from './sign-order-date.utils';

@Component({
  selector: 'app-sign-order-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NzAlertComponent,
    NzButtonModule,
    NzIconModule,
    NzModalModule,
    NzPaginationModule,
    NzSpinModule,
    NzTableModule,
    NzTagModule,
    TranslateModule,
  ],
  templateUrl: './sign-order-list.component.html',
  styleUrls: ['./sign-order-list.component.less'],
})
export class SignOrderListComponent implements OnInit, OnDestroy {
  readonly formatSignOrderDate = formatSignOrderDate;

  @Input() embedded = false;
  @Output() selectOrder = new EventEmitter<string>();
  @Output() newOrder = new EventEmitter<void>();
  @Output() resumeDraft = new EventEmitter<string>();

  orders: OrderListItem[] = [];
  page = 1;
  pageSize = 20;
  total = 0;
  isLoading = true;
  errorMessage: string | null = null;
  canCreate = false;

  drafts: SignOrderDraftSummary[] = [];
  draftPage = 1;
  readonly draftPageSize = 20;
  draftTotal = 0;
  isDraftLoading = true;
  draftErrorMessage: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private signOrderService: SignOrderService,
    private authService: AuthService,
    private router: Router,
    private modal: NzModalService,
    private message: NzMessageService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.canCreate = this.authService.hasAllPermissions(['company.sign_orders.create', 'company.properties.read']);
    this.loadOrders();
    this.loadDrafts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadOrders(page?: number): void {
    if (page) this.page = page;
    this.isLoading = true;
    this.errorMessage = null;

    this.signOrderService.listOrders(this.page, this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.orders = res.items;
          this.total = res.total;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = this.translate.instant('SIGN_ORDER.HISTORY.ERROR_LOADING');
        },
      });
  }

  formatCents(cents: number, currency: string): string {
    const lang = this.translate.currentLang === 'de' ? 'de-DE' : 'en-US';
    const amount = (cents / 100).toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${amount} ${currency}`;
  }

  getDrillingLabel(option: SignOrderDraftSummary['drillingOption']): string {
    return this.translate.instant(option === 'with_drilling'
      ? 'SIGN_ORDER.CHECKOUT.DRILLING_WITH'
      : 'SIGN_ORDER.CHECKOUT.DRILLING_WITHOUT');
  }

  loadDrafts(page?: number): void {
    if (page) this.draftPage = page;
    this.isDraftLoading = true;
    this.draftErrorMessage = null;

    this.signOrderService.listDrafts(this.draftPage, this.draftPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.drafts = response.items;
          this.draftTotal = response.total;
          this.draftPage = response.page;
          this.isDraftLoading = false;
        },
        error: () => {
          this.isDraftLoading = false;
          this.draftErrorMessage = this.translate.instant('SIGN_ORDER.DRAFTS.ERROR_LOADING');
        },
      });
  }

  onResumeDraft(publicId: string): void {
    if (this.embedded) {
      this.resumeDraft.emit(publicId);
      return;
    }
    this.router.navigate(['/qr-poster-template'], {
      queryParams: { signOrderDraftPublicId: publicId },
    });
  }

  confirmDeleteDraft(draft: SignOrderDraftSummary): void {
    this.modal.confirm({
      nzTitle: this.translate.instant('SIGN_ORDER.DRAFTS.DELETE_CONFIRMATION_TITLE'),
      nzContent: this.translate.instant('SIGN_ORDER.DRAFTS.DELETE_CONFIRMATION_TEXT'),
      nzOkText: this.translate.instant('SIGN_ORDER.DRAFTS.DELETE'),
      nzOkDanger: true,
      nzCancelText: this.translate.instant('COMMON.CANCEL'),
      nzOnOk: () => this.deleteDraft(draft),
    });
  }

  private deleteDraft(draft: SignOrderDraftSummary): Promise<void> {
    return new Promise((resolve) => {
      this.signOrderService.deleteDraft(draft.publicId, draft.revision)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.message.success(this.translate.instant('SIGN_ORDER.DRAFTS.DELETED'));
            const lastItemOnPage = this.drafts.length === 1 && this.draftPage > 1;
            this.loadDrafts(lastItemOnPage ? this.draftPage - 1 : this.draftPage);
            resolve();
          },
          error: (error) => {
            if (error?.error?.code === 'DRAFT_VERSION_CONFLICT') {
              this.message.warning(this.translate.instant('SIGN_ORDER.DRAFTS.VERSION_CONFLICT_LIST'));
              this.loadDrafts(this.draftPage);
            } else {
              this.message.error(this.translate.instant('SIGN_ORDER.DRAFTS.ERROR_DELETE'));
            }
            resolve();
          },
        });
    });
  }

  localizedText(text: { de: string; en: string; [key: string]: string } | undefined): string {
    if (!text) return '';
    const lang = this.translate.currentLang || 'de';
    return text[lang] || text.de || text.en || '';
  }

  getStatusLabel(status: string): string {
    return this.translate.instant(`SIGN_ORDER.STATUS.${status.toUpperCase()}`);
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      pending_payment: 'orange',
      payment_received: 'blue',
      processing: 'processing',
      ordered_from_supplier: 'cyan',
      received_from_supplier: 'geekblue',
      shipped: 'purple',
      completed: 'green',
      cancelled: 'red',
    };
    return colors[status] || 'default';
  }

  onNewOrderClick(): void {
    if (this.embedded) {
      this.newOrder.emit();
    } else {
      this.router.navigate(['/qr-poster-template']);
    }
  }
}
